import React from "react";

import { DocScanStatistics } from "models";

import { Card, Flex, Icon, TooltipWrapper, Typography } from "components";

// ─── Color helpers ────────────────────────────────────────────────────────────

const STAT_COLORS: Record<string, string> = {
  match: "#1677ff",
  mismatch: "#ff4d4f",
  ambiguous: "#fa8c16",
  missing_data: "#8c8c8c",
  new_data: "#722ed1",
};

const getStatColor = (status: string) => STAT_COLORS[status] ?? "#8c8c8c";

const MATCH_METHOD_HINTS: Record<string, string> = {
  Programmatic: "Number of fields matched programmatically",
  Blended: "Number of fields matched by programmatically + LLM",
};

const getMatchMethodHint = (method: string) => MATCH_METHOD_HINTS[method] ?? "Number of fields matched by this method";

// ─── Sub-components ───────────────────────────────────────────────────────────

export const StatHeroItem = ({ label, hint, value, color, subtitle }: { label: string; hint: string; value: number; color?: string; subtitle: string }) => (
  <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: "16px" }}>
    <Flex gap={4} align="center" style={{ marginBottom: 2 }}>
      <Typography variant="paragraph-s" className="grey-text">
        {label}
      </Typography>
      <TooltipWrapper tooltip={{ title: hint, placement: "top" }}>
        <Icon iconName="tabler/info-circle" size="xs" className="grey-text pointer" />
      </TooltipWrapper>
    </Flex>
    <div style={{ fontSize: 36, fontWeight: 700, lineHeight: 1.1, color: color ?? "inherit" }}>{value}</div>
    <Typography variant="paragraph-s" className="grey-text">
      {subtitle}
    </Typography>
  </div>
);

export const ReviewMiniCard = ({ label, hint, value, color }: { label: string; hint: string; value: number | string; color?: string }) => (
  <div style={{ border: "1px solid #f0f0f0", borderRadius: 8, padding: "8px 12px" }}>
    <Flex gap={4} align="center" style={{ marginBottom: 2 }}>
      <Typography variant="paragraph-s" className="grey-text">
        {label}
      </Typography>
      <TooltipWrapper tooltip={{ title: hint, placement: "top" }}>
        <Icon iconName="tabler/info-circle" size="xs" className="grey-text pointer" />
      </TooltipWrapper>
    </Flex>
    <div style={{ fontSize: 22, fontWeight: 700, color: color ?? "inherit" }}>{value}</div>
  </div>
);

// ─── Main card ────────────────────────────────────────────────────────────────

type DocScanStatisticsCardProps = {
  statistics: DocScanStatistics;
  totalFarms?: number;
};

export const DocScanStatisticsCard = ({ statistics, totalFarms }: DocScanStatisticsCardProps) => {
  const { total, inDb, matchedByGroups, overallStatusGroups, review } = statistics;

  const matchGroup = overallStatusGroups.find((g) => g.overallStatus === "match");
  const mismatchGroup = overallStatusGroups.find((g) => g.overallStatus === "mismatch");

  const matchCount = matchGroup?.count ?? 0;
  const mismatchCount = mismatchGroup?.count ?? 0;
  const matchRate = total > 0 ? +((matchCount / total) * 100).toFixed(1) : 0;
  const mismatchRate = total > 0 ? +((mismatchCount / total) * 100).toFixed(1) : 0;

  const { reviewed, correct, incorrect, truePositive, trueNegative, falsePositive, falseNegative } = review;
  const accuracy = reviewed > 0 ? `${+((correct / reviewed) * 100).toFixed(1)}%` : "0%";

  const totalSubtitle = inDb === total ? "All in DB" : `${statistics.notInDb} not in DB`;

  const hasTotalFarms = totalFarms !== undefined;
  const heroColCount = (hasTotalFarms ? 1 : 0) + 3 + matchedByGroups.length;

  return (
    <Card className="list-card outlined padding-150 margin-bottom-200">
      <Flex className="padding-bottom-100" align="center" justify="space-between">
        <Typography variant="h3">Statistics</Typography>
      </Flex>

      {/* Hero row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${heroColCount}, 1fr)`,
          gap: 16,
          paddingTop: 8,
          paddingBottom: 24,
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        {hasTotalFarms && (
          <StatHeroItem label="Total Farms" hint="Total number of farms that have at least one doc-scan job." value={totalFarms!} subtitle="" />
        )}
        <StatHeroItem label="Total Fields" hint="Total number of fields extracted from the document for this job." value={total} subtitle={totalSubtitle} />
        <StatHeroItem
          label="Matched"
          hint="Fields where the overall status is match."
          value={matchCount}
          color="#1677ff"
          subtitle={`${matchRate}% match rate`}
        />
        <StatHeroItem
          label="Mismatches"
          hint="Fields where the overall status is mismatch."
          value={mismatchCount}
          color="#ff4d4f"
          subtitle={`${mismatchRate}% of total`}
        />
        {matchedByGroups.map(({ matchedBy, count, percentage }) => (
          <StatHeroItem
            key={matchedBy}
            label={matchedBy}
            hint={getMatchMethodHint(matchedBy)}
            value={count}
            color="#065c45"
            subtitle={`${percentage}% match rate`}
          />
        ))}
      </div>

      {/* Status Breakdown */}
      <div style={{ paddingTop: 16, paddingBottom: 16, borderBottom: "1px solid #f0f0f0" }}>
        <Typography
          variant="paragraph-s"
          bold
          style={{ textTransform: "uppercase", letterSpacing: "0.06em", color: "#8c8c8c", display: "block", marginBottom: 16 }}
        >
          Status Breakdown
        </Typography>
        <Flex vertical gap={12}>
          {overallStatusGroups
            .slice()
            .sort((a, b) => b.count - a.count)
            .map(({ overallStatus, count }) => {
              const pct = total > 0 ? +((count / total) * 100).toFixed(2) : 0;
              const color = getStatColor(overallStatus);
              const rawLabel = overallStatus.replace(/_/g, " ");
              const statusLabel = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);
              return (
                <Flex key={overallStatus} align="center" gap={8}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
                  <Typography variant="paragraph-s" style={{ minWidth: 110 }}>
                    {statusLabel}
                  </Typography>
                  <div style={{ flex: 1, background: "#f0f0f0", height: 6, borderRadius: 3 }}>
                    <div style={{ width: `${pct}%`, background: color, height: "100%", borderRadius: 3 }} />
                  </div>
                  <Typography variant="paragraph-s" bold style={{ minWidth: 28, textAlign: "right" }}>
                    {count}
                  </Typography>
                  <Typography variant="paragraph-s" className="grey-text" style={{ minWidth: 52, textAlign: "right" }}>
                    {pct}%
                  </Typography>
                </Flex>
              );
            })}
        </Flex>
      </div>

      {/* Review Summary */}
      <div style={{ paddingTop: 16 }}>
        <Typography
          variant="paragraph-s"
          bold
          style={{ textTransform: "uppercase", letterSpacing: "0.06em", color: "#8c8c8c", display: "block", marginBottom: 16 }}
        >
          Review Summary
        </Typography>
        {/* Row 1: Reviewed, Correct, Incorrect, Accuracy */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 8 }}>
          <ReviewMiniCard label="Reviewed" hint="Fields where an admin has set isCorrect (true or false)." value={reviewed} />
          <ReviewMiniCard label="Correct" hint="Fields marked as correct by admin (isCorrect = true)." value={correct} color="#1677ff" />
          <ReviewMiniCard label="Incorrect" hint="Fields marked as incorrect by admin (isCorrect = false)." value={incorrect} color="#ff4d4f" />
          <ReviewMiniCard
            label="Accuracy"
            hint="How often the verifier agreed with the system's result, across all reviewed fields"
            value={accuracy}
            color="#065c45"
          />
        </div>
        {/* Row 2: TP / FP / TN / FN */}
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
            <ReviewMiniCard label="True Match" hint="overallStatus is match AND verifier marked as correct" value={truePositive} color="#065c45" />
            <ReviewMiniCard label="False Match" hint="overallStatus is match BUT verifier marked as not correct" value={trueNegative} color="#fa8c16" />
            <ReviewMiniCard label="True Mismatch" hint="overallStatus is mismatch AND verifier marked as correct" value={falsePositive} color="#1677ff" />
            <ReviewMiniCard label="False Mismatch" hint="overallStatus is mismatch BUT verifier marked as not correct" value={falseNegative} color="#ff4d4f" />
          </div>
        </div>
      </div>
    </Card>
  );
};
